import type { ChartOption } from '@app-types/chart';
import type { DividendChartData } from '@app-types/performanceDividend';
import { CHART_AXIS_LABEL_STYLE, CHART_COLORS } from './chartConstants';

/**
 * Builds an ECharts option for the dividend combo chart:
 * red bar series (left y-axis, dividends) + blue line series (right y-axis, stock price).
 * Used by DividendHistoryChart (PD05).
 */
export function buildDividendHistoryOption(data: DividendChartData): ChartOption {
  return {
    grid: { left: 8, right: 8, top: 16, bottom: 8, containLabel: true },
    tooltip: {
      trigger: 'axis',
      // work_items_104: keep the tooltip inside the compact SP chart canvas,
      // especially when ECharts tries to place it left of the first point.
      confine: true,
    },
    xAxis: {
      type: 'category',
      data: data.points.map((p) => p.label),
      axisTick: { show: false },
      axisLine: { lineStyle: { color: CHART_COLORS.grid } },
      axisLabel: CHART_AXIS_LABEL_STYLE,
    },
    yAxis: [
      {
        type: 'value',
        min: 0,
        max: data.leftAxisMax,
        interval: data.leftAxisInterval,
        axisLabel: CHART_AXIS_LABEL_STYLE,
        splitLine: { lineStyle: { color: CHART_COLORS.grid } },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      {
        type: 'value',
        min: data.rightAxisMin,
        max: data.rightAxisMax,
        interval: data.rightAxisInterval,
        axisLabel: CHART_AXIS_LABEL_STYLE,
        splitLine: { show: false },
        axisLine: { show: false },
        axisTick: { show: false },
      },
    ],
    series: [
      {
        type: 'bar',
        name: data.dividendSeriesName,
        data: data.points.map((p) => p.dividend),
        yAxisIndex: 0,
        itemStyle: { color: data.dividendColor },
        barWidth: 20,
      },
      {
        type: 'line',
        name: data.stockSeriesName,
        data: data.points.map((p) => p.stockPrice),
        yAxisIndex: 1,
        lineStyle: { color: data.stockColor, width: 2 },
        itemStyle: { color: data.stockColor },
        symbol: 'circle',
        symbolSize: 4,
        showSymbol: true,
      },
    ],
  };
}
